import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api.client'

export interface IdmRequestRecord {
	requestId: string
	email: string
	action: string
	status: string
	signals: Array<{ severity: string; code: string; message: string; requiresOverride: boolean }>
	confirmationPayload: Record<string, unknown> | null
	executionResult: Record<string, unknown> | null
	expiresAt: string
	confirmedAt: string | null
	completedAt: string | null
	cancelledAt: string | null
	createdAt: string
	updatedAt: string
}

export const useIdmRequests = (status?: string) =>
	useQuery({
		queryKey: ['idm-requests', status],
		queryFn: async () => {
			const params = status ? `?filters[status]=${status}` : ''
			const response = await apiClient.get<IdmRequestRecord[]>(`/employees/idm-requests${params}`)
			return response.data || []
		},
	})

export const useIdmRequest = (requestId: string) =>
	useQuery({
		queryKey: ['idm-request', requestId],
		queryFn: async () => {
			const response = await apiClient.get<IdmRequestRecord>(`/employees/idm-requests/${requestId}`)
			return response.data
		},
		enabled: !!requestId,
	})

export const useConfirmIdmRequest = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({
			requestId,
			overrideSignals,
			overrideJustification,
		}: {
			requestId: string
			overrideSignals?: string[]
			overrideJustification?: string
		}) => {
			const response = await apiClient.post<{ requestId: string; status: string }>(
				`/employees/idm-requests/${requestId}/confirm`,
				{
					confirmed: true,
					overrideSignals,
					overrideJustification,
				},
			)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['idm-requests'] }),
	})
}

export interface AuditEvent {
	eventId: string
	type: string
	entity: string
	action: string
	source: string
	correlationId: string | null
	userId: string | null
	data: Record<string, unknown>
	timestamp: string
}

export const useIdmAuditTrail = (requestId: string) =>
	useQuery({
		queryKey: ['idm-audit-trail', requestId],
		queryFn: async () => {
			// Query events linked to this specific IDM request via entityType + entityId
			const response = await apiClient.get<AuditEvent[]>(
				`/events?filters[entityType]=idm_request&filters[entityId]=${requestId}`,
			)
			return response.data || []
		},
		enabled: !!requestId,
	})

export const useCancelIdmRequest = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (requestId: string) => {
			const response = await apiClient.post<{ requestId: string; status: string }>(
				`/employees/idm-requests/${requestId}/cancel`,
				{},
			)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['idm-requests'] }),
	})
}
